import {BlobReader, BlobWriter, ZipReader} from '@zip.js/zip.js';

const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.webp']);
const MAX_SOURCE_BYTES = 50 * 1024 * 1024;
const MAX_ARCHIVE_BYTES = 2 * 1024 * 1024 * 1024;
const MAX_UNCOMPRESSED_BYTES = 10 * 1024 * 1024 * 1024;
const MAX_ARCHIVE_ENTRIES = 20000;
const MAX_ITEMS = 5000;

function extension(path: string) {
    const match = String(path).toLowerCase().match(/\.[^./]+$/);
    return match ? match[0] : '';
}

function basename(path: string) {
    return String(path).split('/').pop() || '';
}

function stem(path: string) {
    return basename(path).replace(/\.[^.]+$/, '');
}

function normalisePath(path: string) {
    return String(path || '').replace(/\\/g, '/').replace(/^\/+/, '');
}

function ignored(path: string) {
    return !path || path.startsWith('__MACOSX/') || basename(path) === '.DS_Store';
}

function contentType(path: string) {
    const ext = extension(path);
    if (ext === '.jpg' || ext === '.jpeg') return 'image/jpeg';
    if (ext === '.png') return 'image/png';
    return 'image/webp';
}

export function clientBatchFileIdentity(file: File) {
    return `${file.name}:${file.size}:${file.lastModified || 0}`;
}

export function clientBatchConfigSignature(config: any) {
    return JSON.stringify({
        categories: [...(config.categories || [])].sort(),
        module: config.module,
        needs_ai_tag: Boolean(config.needs_ai_tag),
        force_resize: Boolean(config.force_resize),
    });
}

export async function openClientBatchArchive(file: File, categoryMap: Record<string, string>, fallbackCategories: string[]) {
    if (file.size > MAX_ARCHIVE_BYTES) throw new Error('单个 ZIP 不能超过 2GB');
    const reader = new ZipReader(new BlobReader(file), {useWebWorkers: true, filenameEncoding: 'utf-8'});
    try {
        const entries: any[] = await reader.getEntries();
        if (entries.length > MAX_ARCHIVE_ENTRIES) throw new Error(`ZIP 内文件数量不能超过 ${MAX_ARCHIVE_ENTRIES}`);
        const groups = new Map<string, {images: any[], animations: any[]}>();
        let imageCount = 0;
        let uncompressedBytes = 0;
        for (const entry of entries) {
            const path = normalisePath(entry.filename);
            if (entry.directory || ignored(path)) continue;
            uncompressedBytes += Number(entry.uncompressedSize || 0);
            if (uncompressedBytes > MAX_UNCOMPRESSED_BYTES) throw new Error('ZIP 解压后的文件总大小不能超过 10GB');
            if (basename(path).toLowerCase() === 'mapping.csv') {
                return {reader, items: [], fallbackReason: '压缩包包含 mapping.csv，需要使用后端配对'};
            }
            const ext = extension(path);
            if (!IMAGE_EXTENSIONS.has(ext) && ext !== '.mp4') continue;
            if (Number(entry.uncompressedSize || 0) > MAX_SOURCE_BYTES) {
                throw new Error(`${path} 超过 50MB`);
            }
            const key = stem(path);
            const group = groups.get(key) || {images: [], animations: []};
            if (ext === '.mp4') group.animations.push(entry);
            else {
                group.images.push(entry);
                imageCount += 1;
            }
            groups.set(key, group);
        }
        if (!imageCount) return {reader, items: [], fallbackReason: '压缩包中没有静态图片'};
        if (imageCount > MAX_ITEMS) throw new Error(`单个 ZIP 最多支持 ${MAX_ITEMS} 张图片`);
        if ([...groups.values()].some(group => group.animations.length && !group.images.length)) {
            return {reader, items: [], fallbackReason: '存在异名或单独 MP4，需要使用后端匹配'};
        }

        const items: any[] = [];
        for (const group of groups.values()) {
            for (const imageEntry of group.images) {
                const path = normalisePath(imageEntry.filename);
                const parts = path.split('/').filter(Boolean);
                const parent = parts.length > 1 ? parts[parts.length - 2] : '';
                items.push({
                    key: path,
                    imageEntry,
                    animationEntry: group.animations[0] || null,
                    imageFilename: basename(path),
                    animationFilename: group.animations[0] ? basename(group.animations[0].filename) : '',
                    imageContentType: contentType(path),
                    categories: categoryMap[parent] ? [categoryMap[parent]] : [...fallbackCategories],
                });
            }
        }
        items.sort((left, right) => left.key.localeCompare(right.key));
        return {reader, items, fallbackReason: ''};
    } catch (error) {
        await reader.close();
        throw error;
    }
}

export async function readClientBatchEntry(entry: any, type: string) {
    return entry.getData(new BlobWriter(type), {useWebWorkers: true});
}

export async function preprocessClientBatchImage(
    blob: Blob,
    filename: string,
    forceResize: boolean,
    signal?: AbortSignal,
): Promise<any> {
    const buffer = await blob.arrayBuffer();
    return new Promise((resolve, reject) => {
        const worker = new Worker(new URL('../workers/batchImage.worker.js', import.meta.url), {type: 'module'});
        const id = `${Date.now()}-${Math.random()}`;
        let settled = false;
        const finish = () => {
            if (settled) return false;
            settled = true;
            signal?.removeEventListener('abort', handleAbort);
            worker.terminate();
            return true;
        };
        const handleAbort = () => {
            if (!finish()) return;
            const error: any = new Error('本机批量任务已取消');
            error.batchUploadCancelled = true;
            reject(error);
        };
        if (signal?.aborted) {
            handleAbort();
            return;
        }
        signal?.addEventListener('abort', handleAbort, {once: true});
        worker.onerror = event => {
            if (!finish()) return;
            reject(new Error(event.message || '浏览器图片处理失败'));
        };
        worker.onmessage = event => {
            if (event.data?.id !== id) return;
            if (!finish()) return;
            if (event.data.error) {
                reject(new Error(event.data.error));
                return;
            }
            resolve({
                image: new File([event.data.imageBuffer], event.data.imageFilename, {type: 'image/webp'}),
                thumbnail: new File([event.data.thumbnailBuffer], event.data.thumbnailFilename, {type: 'image/webp'}),
            });
        };
        worker.postMessage({id, buffer, contentType: blob.type, filename, forceResize}, [buffer]);
    });
}
